import { focusStory, heatList, quickEntries } from '@/mocks/exposure';
import { Dot, Tag } from './ui';

const trendIcon: Record<string, string> = {
  up: 'ri-arrow-up-line',
  flat: 'ri-arrow-right-up-line',
  new: 'ri-sparkling-2-line',
};

const entryTheme: Record<string, string> = {
  primary: 'bg-primary-500 text-background-50 hover:bg-primary-600',
  secondary: 'bg-secondary-500 text-background-50 hover:bg-secondary-600',
  accent: 'bg-accent-500 text-background-50 hover:bg-accent-600',
  dark: 'bg-foreground-900 text-background-50 hover:bg-foreground-800',
};

export default function FocusSection() {
  return (
    <section id="focus" className="mx-auto w-full max-w-[1280px] px-4 pt-9 pb-3 md:px-6 md:pt-12">
      <div className="grid gap-6 lg:grid-cols-3">
        {/* 左：焦点大图卡片（标题优先） */}
        <a
          href={focusStory.href}
          className="group relative overflow-hidden rounded-xl bg-foreground-900 lg:col-span-2"
        >
          <img
            src="https://readdy.ai/api/search-image?query=Dark%20editorial%20photojournalism%20collage%20of%20torn%20printed%20chat%20pages%2C%20blurred%20smartphone%20fragments%20and%20redacted%20documents%20layered%20on%20dark%20desk%2C%20warm%20crimson%20and%20charcoal%20palette%2C%20dramatic%20side%20lighting%2C%20paper%20texture%2C%20forensic%20investigation%20mood%2C%20cinematic%20high%20contrast%2C%20no%20identifiable%20faces&width=1280&height=760&seq=hb-focus-01&orientation=landscape"
            alt="私下录音完整流出：台前人设判若两人"
            className="h-full w-full object-cover object-top opacity-90 transition duration-500 group-hover:scale-[1.03]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950 via-foreground-950/40 to-transparent" />
          <div className="absolute inset-x-0 bottom-0 p-5 md:p-7">
            <div className="mb-3 flex flex-wrap items-center gap-2">
              <Tag tone="primary">{focusStory.tag}</Tag>
              <span className="inline-flex items-center gap-1 rounded-full bg-foreground-950/60 px-2 py-0.5 text-[11px] font-medium text-background-100 backdrop-blur">
                <i className="ri-time-line" /> 完整转写已归档
              </span>
            </div>
            <h1 className="font-serif text-xl font-black leading-snug text-background-50 md:text-3xl md:leading-tight">
              {focusStory.title}
            </h1>
            <p className="mt-3 hidden max-w-2xl text-sm leading-relaxed text-background-200 md:block">
              {focusStory.excerpt}
            </p>
            <div className="mt-4 flex flex-wrap items-center gap-4 text-xs text-background-300 md:text-sm">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-fire-fill text-accent-400" /> {focusStory.meta}
              </span>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-background-100/25 px-2.5 py-1 font-medium text-background-100">
                <i className="ri-play-circle-line" /> 时长 {focusStory.duration}
              </span>
            </div>
          </div>
        </a>

        {/* 右：热度实锤榜 */}
        <div className="flex flex-col rounded-xl border border-background-200 bg-background-50">
          <div className="flex items-center justify-between border-b border-background-200 px-4 py-3 md:px-5">
            <div className="flex items-center gap-2">
              <i className="ri-fire-line text-lg text-primary-500" />
              <h2 className="font-serif text-base font-black text-foreground-900 md:text-lg">
                全网热度实锤榜
              </h2>
            </div>
            <Dot className="bg-accent-500 animate-pulse-dot" />
          </div>
          <ol className="flex flex-1 flex-col">
            {heatList.map((it, idx) => (
              <li key={it.no} className={idx > 0 ? 'border-t border-background-100' : ''}>
                <a
                  href="#chat"
                  className="group flex gap-3 px-4 py-3 transition hover:bg-background-100/70 md:px-5"
                >
                  <span
                    className={`font-serif text-lg font-black leading-6 ${
                      idx < 3 ? 'text-primary-500' : 'text-secondary-300'
                    }`}
                  >
                    {it.no}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-sm font-medium text-foreground-800 group-hover:text-primary-700">
                      {it.title}
                    </span>
                    <span className="mt-1 flex items-center gap-2 text-[11px] text-foreground-400">
                      <i className="ri-fire-fill text-accent-500" />
                      <span className="font-semibold text-foreground-600">{it.heat}</span>
                      <span className="inline-flex items-center gap-0.5">
                        <i className={trendIcon[it.trend]} /> {it.diff}
                      </span>
                      <Tag tone="secondary">{it.tag}</Tag>
                    </span>
                  </span>
                </a>
              </li>
            ))}
          </ol>
          <a
            href="#index"
            className="flex items-center justify-between border-t border-background-200 px-4 py-3 text-sm font-medium text-foreground-600 transition hover:text-primary-600 md:px-5"
          >
            查看完整榜单
            <i className="ri-arrow-right-s-line text-lg" />
          </a>
        </div>
      </div>

      {/* 四大材料专区 · 醒目入口条 */}
      <div className="mt-6 grid grid-cols-2 gap-3 lg:grid-cols-4 md:gap-4">
        {quickEntries.map((e) => (
          <a
            key={e.key}
            href={`#${e.key}`}
            className={`group flex flex-col gap-3 rounded-xl p-4 transition md:p-5 ${entryTheme[e.theme]}`}
          >
            <span className="flex h-11 w-11 items-center justify-center rounded-lg bg-background-50/15 text-xl backdrop-blur">
              <i className={e.icon} />
            </span>
            <span>
              <span className="flex items-center justify-between">
                <span className="font-serif text-lg font-black">{e.name}</span>
                <i className="ri-arrow-right-up-line text-base opacity-70 transition group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
              </span>
              <span className="mt-1.5 block text-xs leading-relaxed opacity-85">{e.desc}</span>
              <span className="mt-2 inline-flex items-center gap-1.5 text-xs font-semibold">
                <i className="ri-folder-open-line" /> 已归档 {e.count}
              </span>
            </span>
          </a>
        ))}
      </div>
    </section>
  );
}